import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CheckCircle2, Loader2, LogOut, Mail, ShieldCheck } from "lucide-react";
import { Input } from "src/components/ui/Input";
import { SlabButton } from "src/components/ui/SlabButton";
import { useOperatorAuthStore } from "src/store/operatorAuthStore";
import { OPERATOR_AUTH_ENV } from "src/hooks/useOperatorSession";
import { selectUser, signOutUser } from "src/store/authSlice";
import type { AppDispatch } from "src/store";
import ThemedPage from "./ThemedPage";
import ShootingMeteors from "./ShootingMeteors";

/**
 * OTP-only screen for the iv3api operator session (the second factor after
 * Google auth). Rendered by ProtectedRoute when Google is authenticated but no
 * valid operator token exists.
 *
 * Behaviour:
 *   - A verification code is sent automatically on mount (no "send" button) to
 *     the signed-in Google email; env is always prod.
 *   - The user enters the code and logs in. On success ProtectedRoute takes
 *     over, plays the rocket launch, and reveals the dashboard.
 *   - "Use a different account" signs out of Google (and clears the operator
 *     token), returning to the Google login screen.
 */
const OperatorAuthGate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const email = user?.email ?? null;

  const auditId = useOperatorAuthStore((s) => s.auditId);
  const pincodeRecipient = useOperatorAuthStore((s) => s.pincodeRecipient);
  const sendingPincode = useOperatorAuthStore((s) => s.sendingPincode);
  const exchanging = useOperatorAuthStore((s) => s.exchanging);
  const error = useOperatorAuthStore((s) => s.error);
  const sendPincode = useOperatorAuthStore((s) => s.sendPincode);
  const exchangeOtp = useOperatorAuthStore((s) => s.exchangeOtp);
  const clearError = useOperatorAuthStore((s) => s.clearError);

  const [otp, setOtp] = useState("");
  const awaitingOtp = Boolean(auditId);

  const send = () => {
    if (!email?.trim()) return;
    clearError();
    void sendPincode(email.trim(), OPERATOR_AUTH_ENV);
  };

  // Auto-send the code once on mount. The ref guards against React's dev
  // double-invoke and re-renders so we don't fire duplicate emails.
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (autoSentRef.current) return;
    if (!email?.trim() || awaitingOtp) return;
    autoSentRef.current = true;
    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleVerify = () => {
    if (!email?.trim() || !awaitingOtp || exchanging || !otp.trim()) return;
    clearError();
    void exchangeOtp(email.trim(), OPERATOR_AUTH_ENV, otp);
  };

  return (
    <ThemedPage initialTheme="liftoff">
      <div className="min-h-screen w-full relative overflow-hidden text-white flex items-center justify-center">
        <ShootingMeteors />
        <div className="relative z-10 w-full max-w-md px-6">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-indigo-500/20 text-indigo-200 mb-3">
                <ShieldCheck className="h-6 w-6" aria-hidden />
              </span>
              <h1 className="text-2xl font-bold mb-1">Verify it&apos;s you</h1>
              <p className="text-gray-300 text-sm">
                Enter the code we just sent to finish signing in.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                <p className="text-red-200 text-sm text-center">{error}</p>
              </div>
            )}

            <div className="mb-4 flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2 text-sm">
              <Mail className="h-4 w-4 shrink-0 text-gray-300" aria-hidden />
              <span className="truncate">
                Code sent to <span className="font-semibold">{email}</span>
              </span>
            </div>

            {sendingPincode && !awaitingOtp ? (
              <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-200">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Sending code…
              </div>
            ) : (
              <div className="space-y-3">
                {awaitingOtp && (
                  <p className="flex items-center gap-1.5 text-xs text-emerald-300">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                    Sent{pincodeRecipient ? ` to ${pincodeRecipient}` : ""}.
                    Check your inbox (and spam).
                  </p>
                )}
                <Input
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 8))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleVerify();
                    }
                  }}
                  placeholder="1234"
                  aria-label="Verification code"
                  className="font-mono text-base tracking-widest"
                  autoFocus
                />
                <SlabButton
                  type="button"
                  variant="primary"
                  disabled={exchanging || !otp.trim()}
                  onClick={handleVerify}
                  className="w-full"
                >
                  {exchanging ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />{" "}
                      Logging in…
                    </>
                  ) : (
                    "Log in"
                  )}
                </SlabButton>
                <button
                  type="button"
                  onClick={send}
                  disabled={sendingPincode}
                  className="w-full text-center text-xs text-gray-300 hover:text-white disabled:opacity-50"
                >
                  {sendingPincode ? "Resending…" : "Didn't get it? Resend code"}
                </button>
              </div>
            )}

            <div className="mt-6 border-t border-white/10 pt-4 text-center">
              <button
                type="button"
                onClick={() => void dispatch(signOutUser())}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-white"
              >
                <LogOut className="h-3.5 w-3.5" aria-hidden />
                Use a different account
              </button>
            </div>
          </div>
        </div>
      </div>
    </ThemedPage>
  );
};

export default OperatorAuthGate;
